import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Send } from "lucide-react";

const fmtDate = (v) => { try { return new Date(v).toLocaleString(); } catch { return String(v); } };

/**
 * Newsletter blast composer + send history (admin).
 * Sends to all current subscribers; every email carries its own unsubscribe link.
 */
export default function AdminBlast({ subscriberCount }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [blasts, setBlasts] = useState(null);

  async function loadBlasts() {
    try {
      const { data } = await api.get("/admin/newsletter/blasts");
      setBlasts(data);
    } catch {
      setBlasts([]);
    }
  }

  useEffect(() => { loadBlasts(); }, []);

  async function send() {
    if (!subject.trim() || !body.trim()) {
      toast.error("Add a subject and a message first.");
      return;
    }
    if (!window.confirm(`Send "${subject}" to ${subscriberCount ?? "all"} subscribers?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/blast", {
        subject: subject.trim(),
        body: body.trim(),
        origin_url: window.location.origin,
      });
      toast.success(`Blast sent — ${data.sent} delivered, ${data.failed} failed.`);
      setSubject("");
      setBody("");
      loadBlasts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the blast.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="mb-6" data-testid="admin-blast-panel">
      <div className="bg-white border border-ink/10 rounded-[14px] p-6">
        <div className="flex items-center gap-2 text-xs font-bold text-palm uppercase tracking-[0.14em] mb-3">
          <Mail size={13} /> Compose newsletter blast
        </div>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject line"
          maxLength={150}
          data-testid="admin-blast-subject"
          className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Write your update for subscribers. Blank lines separate paragraphs. Every email includes an unsubscribe link automatically."
          rows={5}
          maxLength={5000}
          data-testid="admin-blast-body"
          className="w-full rounded-md border border-ink/15 px-3 py-2.5 mb-3 focus:outline-none focus:border-palm"
        />
        <button
          type="button"
          onClick={send}
          disabled={sending}
          data-testid="admin-blast-send-btn"
          className="inline-flex items-center gap-2 bg-palm text-white font-bold rounded-full px-5 py-2.5 hover:shadow-xl transition disabled:opacity-60"
        >
          {sending ? <><Loader2 size={15} className="animate-spin" /> Sending…</> : <><Send size={15} /> Send to {subscriberCount ?? "…"} subscribers</>}
        </button>
      </div>

      {blasts && blasts.length > 0 && (
        <div className="mt-4" data-testid="admin-blast-history">
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink/50 mb-2">Previous blasts</div>
          <div className="space-y-2">
            {blasts.map((b) => (
              <div key={b.id} className="bg-white border border-ink/10 rounded-[10px] px-4 py-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-semibold">{b.subject}</span>
                <span className="text-ink/60">{b.sent}/{b.total} sent{b.failed ? ` · ${b.failed} failed` : ""}</span>
                <span className="text-ink/50 text-xs ml-auto">{fmtDate(b.created_at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
